/**
 * Design tokens for voice-chat-ui components
 *
 * These tokens provide a single source of truth for styling values
 * that match the Shadcn/Tailwind design system used in @workspace/ui.
 *
 * Usage: Import and use in inline styles for Preact/React components
 * that need to match the web app's design without Tailwind.
 */

// =============================================================================
// Colors (CSS Variable References)
// =============================================================================

/**
 * Color tokens that reference CSS variables.
 * These work at runtime with the theme's CSS variables.
 */
export const colors = {
  // Core colors
  background: "var(--background)",
  foreground: "var(--foreground)",

  // Card colors
  card: "var(--card)",
  cardForeground: "var(--card-foreground)",

  // Popover colors
  popover: "var(--popover)",
  popoverForeground: "var(--popover-foreground)",

  // Primary colors
  primary: "var(--primary)",
  primaryForeground: "var(--primary-foreground)",

  // Secondary colors
  secondary: "var(--secondary)",
  secondaryForeground: "var(--secondary-foreground)",

  // Muted colors
  muted: "var(--muted)",
  mutedForeground: "var(--muted-foreground)",

  // Accent colors
  accent: "var(--accent)",
  accentForeground: "var(--accent-foreground)",

  // Destructive colors
  destructive: "var(--destructive)",
  destructiveForeground: "var(--destructive-foreground)",

  // Warning colors
  warning: "var(--warning)",

  // Border and input colors
  border: "var(--border)",
  input: "var(--input)",
  ring: "var(--ring)",
} as const;

/**
 * Helper to create color values with opacity using color-mix
 * Works with oklch, hsl, or any CSS color values
 * Usage: colorWithOpacity(colors.primary, 0.5)
 */
export function colorWithOpacity(colorVar: string, opacity: number): string {
  return `color-mix(in oklch, ${colorVar} ${Math.round(opacity * 100)}%, transparent)`;
}

// =============================================================================
// Spacing
// =============================================================================

/**
 * Spacing scale matching Tailwind's default spacing
 * Values in pixels
 */
export const spacing = {
  0: 0,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  3.5: 14,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  9: 36,
  10: 40,
  11: 44,
  12: 48,
  14: 56,
  16: 64,
  20: 80,
  24: 96,
  28: 112,
  32: 128,
} as const;

// =============================================================================
// Typography
// =============================================================================

/**
 * Font sizes matching Tailwind's text-* classes
 * Values are [fontSize, lineHeight]
 */
export const fontSize = {
  xs: { size: 12, lineHeight: 16 },
  sm: { size: 14, lineHeight: 20 },
  base: { size: 16, lineHeight: 24 },
  lg: { size: 18, lineHeight: 28 },
  xl: { size: 20, lineHeight: 28 },
  "2xl": { size: 24, lineHeight: 32 },
  "3xl": { size: 30, lineHeight: 36 },
  "4xl": { size: 36, lineHeight: 40 },
} as const;

/**
 * Font weights matching Tailwind's font-* classes
 */
export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/**
 * Font family stack
 */
export const fontFamily = {
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
} as const;

// =============================================================================
// Border Radius
// =============================================================================

/**
 * Border radius values matching Shadcn's radius scale
 * Base radius is 0.625rem (10px)
 */
export const radius = {
  none: 0,
  sm: 6, // calc(var(--radius) - 4px)
  md: 8, // calc(var(--radius) - 2px)
  lg: 10, // var(--radius)
  xl: 14, // calc(var(--radius) + 4px)
  full: 9999,
} as const;

// =============================================================================
// Shadows
// =============================================================================

/**
 * Box shadow values matching Tailwind/Shadcn shadows
 */
export const shadows = {
  none: "none",
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  DEFAULT: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
} as const;

// =============================================================================
// Transitions
// =============================================================================

/**
 * Transition duration values in milliseconds
 */
export const duration = {
  75: 75,
  100: 100,
  150: 150,
  200: 200,
  300: 300,
  500: 500,
  700: 700,
  1000: 1000,
} as const;

/**
 * Transition timing functions
 */
export const easing = {
  linear: "linear",
  in: "cubic-bezier(0.4, 0, 1, 1)",
  out: "cubic-bezier(0, 0, 0.2, 1)",
  inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

// =============================================================================
// Z-Index
// =============================================================================

/**
 * Z-index scale for layering
 */
export const zIndex = {
  0: 0,
  10: 10,
  20: 20,
  30: 30,
  40: 40,
  50: 50,
  dropdown: 1000,
  modal: 1100,
  popover: 1200,
  tooltip: 1300,
} as const;

// =============================================================================
// Component-Specific Tokens
// =============================================================================

/**
 * Button specific tokens
 */
export const button = {
  height: {
    sm: 32,
    default: 36,
    lg: 40,
    icon: 36,
  },
  padding: {
    sm: { x: 12, y: 0 },
    default: { x: 16, y: 8 },
    lg: { x: 32, y: 0 },
    icon: { x: 0, y: 0 },
  },
  fontSize: {
    sm: fontSize.xs.size,
    default: fontSize.sm.size,
    lg: fontSize.sm.size,
  },
  borderRadius: radius.md,
} as const;

/**
 * Card specific tokens
 */
export const card = {
  borderRadius: radius.lg + 2, // 12px for cards
  padding: spacing[6],
  shadow: shadows.DEFAULT,
} as const;

/**
 * Dropdown/Popover specific tokens
 */
export const dropdown = {
  borderRadius: radius.md,
  padding: spacing[1],
  itemHeight: 32,
  itemPadding: { x: 8, y: 6 },
  shadow: shadows.md,
  minWidth: 128,
} as const;

// =============================================================================
// Export all tokens as a single object for convenience
// =============================================================================

export const tokens = {
  colors,
  spacing,
  fontSize,
  fontWeight,
  fontFamily,
  radius,
  shadows,
  duration,
  easing,
  zIndex,
  button,
  card,
  dropdown,
} as const;

export default tokens;
